import { useEffect, useRef, useState } from 'react'
import {Loader2, Save, Upload} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import { nowISO } from '@/utils/masks'

const CHAVE_GLOBAL = 'GLOBAL'

function Configuracoes() {
  const { user: currentUser } = useAppAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [configId, setConfigId] = useState<string | null>(null)
  const [nomeSistema, setNomeSistema] = useState('PROTOCOLEAI')
  const [logoUrl, setLogoUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  const carregarConfig = async () => {
    setLoading(true)
    try {
      const r = await (lumi.entities as any).system_config.list({
        filter: { chave: CHAVE_GLOBAL },
        limit: 1,
      })
      const config = r?.list?.[0]
      if (config) {
        setConfigId(config._id)
        setNomeSistema(config.nomeSistema || 'PROTOCOLEAI')
        setLogoUrl(config.logoUrl || '')
      }
    }
    catch {
      toast.error('Erro ao carregar as configurações.')
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    carregarConfig()
  }, [])

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results?.[0]
      if (result?.fileUrl) {
        setLogoUrl(result.fileUrl)
        toast.success('Logo enviado com sucesso.')
      }
      else {
        toast.error(result?.uploadError || 'Erro ao enviar o logo.')
      }
    }
    catch {
      toast.error('Erro ao enviar o logo. Tente novamente.')
    }
    finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleSalvar = async () => {
    if (!nomeSistema.trim()) {
      toast.error('O nome do sistema é obrigatório.')
      return
    }
    setSaving(true)
    try {
      const payload = {
        nomeSistema: nomeSistema.trim(),
        logoUrl: logoUrl.trim(),
        updatedAt: nowISO(),
      }

      if (configId) {
        await (lumi.entities as any).system_config.update(configId, payload)
      }
      else {
        const created = await (lumi.entities as any).system_config.create({
          chave: CHAVE_GLOBAL,
          ...payload,
          creator: currentUser?.userId || 'system',
          createdAt: nowISO(),
        })
        setConfigId(created?._id || null)
      }
      toast.success('Configurações guardadas com sucesso.')
    }
    catch {
      toast.error('Erro ao guardar as configurações. Tente novamente.')
    }
    finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#1E4FBF] animate-spin" />
      </div>
    )
  }

  return (
    <div className="max-w-[640px]">
      <div className="mb-6">
        <h1 className="text-[20px] font-semibold text-[#12141A]">Configurações Globais</h1>
        <p className="text-[13.5px] text-[#6B7280] mt-1">
          Defina o nome e o logo padrão do sistema, exibidos quando uma cidade não possuir configuração própria.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E7E9EC] p-6 flex flex-col gap-5">
        <div>
          <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Nome do Sistema</label>
          <input
            value={nomeSistema}
            onChange={e => setNomeSistema(e.target.value)}
            placeholder="PROTOCOLEAI"
            className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
          />
          <p className="text-[12px] text-[#9CA3AF] mt-1">
            Nome exibido por padrão no login e nos documentos, salvo quando a cidade define o seu próprio.
          </p>
        </div>

        <div>
          <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Logo do Sistema</label>
          <div className="flex items-center gap-3">
            {logoUrl && (
              <img src={logoUrl} alt="Logo" className="w-14 h-14 rounded-lg object-cover border border-[#E7E9EC]" />
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13.5px] font-medium border border-[#D1D5DB] hover:bg-[#F5F6F8] disabled:opacity-60"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.75} />}
              {uploading ? 'A enviar...' : logoUrl ? 'Alterar logo' : 'Enviar logo (PNG)'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleLogoUpload}
              className="hidden"
            />
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-[#E7E9EC]">
          <button
            onClick={handleSalvar}
            disabled={saving || uploading}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#1E4FBF] hover:bg-[#1A44A6] text-white disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.75} />}
            {saving ? 'A guardar...' : 'Guardar Configurações'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Configuracoes
